"use client";

import { AppLink } from "@/components/atoms/AppLink";
import { AppPriceStatus } from "@/components/atoms/AppPriceStatus";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/atoms/Card";
import PopularAppLogo from "@/components/atoms/Icon/popularApp";
import { Paragraph } from "@/components/atoms/Paragraph";
import { Picture } from "@/components/atoms/Picture";
import { Rating } from "@/components/atoms/Rating";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/atoms/Tooltip";
import type { IApplicationCardProps } from "@/components/molecules/ApplicationCard/types";
import priceCounterWithTrialDays from "@/helpers/priceCounterWithTrialDays";
import { useAuthUser } from "@/hooks/common/useAuthUser";
import { useTranslations } from "@/hooks/common/useTranslations";

const ApplicationCard = ({
  application: {
    logoUrl,
    price,
    monthlyPrice,
    isPopular,
    trialDays,
    alias,
    name,
    description,
    averageRating,
    formattedPrice,
    formattedMonthlyPrice,
    reviewsCount,
  },
}: IApplicationCardProps) => {
  const { t } = useTranslations("common");
  const { workspaceConfig } = useAuthUser();

  const { freeApp, haveTrialDays, monthlyOrNot } = priceCounterWithTrialDays(
    price,
    monthlyPrice,
    trialDays,
  );

  const applicationPrice = monthlyOrNot
    ? `${formattedPrice}`
    : `${formattedMonthlyPrice} ${t("monthly")}.`;

  const appFreeOrPayable = freeApp ? t("free") : applicationPrice;
  const type = freeApp ? "free" : "notFree";
  const rating = averageRating > 0 ? +(5 / +averageRating).toFixed(2) : 0;

  return (
    <AppLink
      href={`/${alias}`}
      title={name}
      className="max-sm:border-b last:border-none"
    >
      <Card className="group w-full hover:bg-primary-light hover:shadow duration-300 cursor-pointer max-sm:border-none">
        <CardHeader>
          <div className="w-full flex justify-between">
            <div className="flex gap-3">
              <Picture width={64} height={64} alt={name} src={logoUrl} />
              <div className="flex flex-col gap-3">
                <Paragraph size="base" fontWeight="medium">
                  {name}
                </Paragraph>
                <div className="flex gap-1">
                  <AppPriceStatus
                    type={type}
                    haveTrialDays={haveTrialDays}
                    trialDays={trialDays}
                    text={appFreeOrPayable}
                  />
                </div>
              </div>
            </div>
            {isPopular && (
              <div className="w-7 h-7 animate-pulse flex items-center justify-center bg-gold rounded-full">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <PopularAppLogo />
                    </TooltipTrigger>
                    <TooltipContent>
                      <Paragraph size="extraSmall" fontWeight="regular">
                        {t("popularApp")}
                      </Paragraph>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="max-sm:pl-[92px] pb-6">
          <Paragraph
            size="small"
            fontWeight="regular"
            type="secondary"
            className="line-clamp-2"
          >
            {description}
          </Paragraph>
        </CardContent>
        <CardFooter className="w-full flex justify-between max-sm:pl-[92px]">
          <Paragraph size="small" fontWeight="regular">
            {t("byCompany", { clientName: workspaceConfig?.clientName || "" })}
          </Paragraph>
          <div className="flex gap-2 items-center">
            <div className="flex gap-1 items-center">
              <Paragraph size="small" fontWeight="regular">
                {averageRating}
              </Paragraph>
              <Rating
                readOnly
                style={{ maxWidth: 17 }}
                items={1}
                value={rating}
              />
            </div>
            <Paragraph size="small" fontWeight="regular" type="secondary">
              {t("feedbackCount", {
                count: reviewsCount,
              })}
            </Paragraph>
          </div>
        </CardFooter>
      </Card>
    </AppLink>
  );
};

ApplicationCard.displayName = "ApplicationCard";

export default ApplicationCard;
